import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { logWarn } from '@/lib/devlog';

export const MAX_ATTEMPTS = 8;
export const POLL_INTERVAL = 2000;

/**
 * Polls Stripe checkout status until a terminal state.
 * Returns { status, attempts }. status ∈ checking | success | expired | error.
 * On 'success', calls onSuccessRefresh() (typically refreshes the logged-in user).
 *
 * Implementation detail: initial state is derived at render-time (no synchronous
 * setState inside useEffect body) so it passes React Compiler immutability rules.
 */
export const useCheckoutPolling = (sessionId, onSuccessRefresh) => {
  const [status, setStatus] = useState(() => (sessionId ? 'checking' : 'error'));
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return undefined; // initial state already 'error'
    let cancelled = false;

    const finish = (next) => { if (!cancelled) setStatus(next); };
    const bumpAttempt = (n) => { if (!cancelled) setAttempts(n); };

    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          finish('success');
          try { await onSuccessRefresh?.(); }
          catch (e) { logWarn('[billing] success refresh failed', e?.message); }
          return;
        }
        if (data.status === 'expired' || data.payment_status === 'failed') {
          finish('expired');
          return;
        }
        if (attempt >= MAX_ATTEMPTS - 1) { finish('error'); return; }
        bumpAttempt(attempt + 1);
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (err) {
        if (attempt >= MAX_ATTEMPTS - 1) { finish('error'); return; }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };
    // Defer the kickoff to a microtask so the rule does not consider it
    // a synchronous setState chain originating from the effect body.
    queueMicrotask(() => poll(0));
    return () => { cancelled = true; };
  }, [sessionId, onSuccessRefresh]);

  return { status, attempts };
};
